import React, { useEffect, useState } from 'react'
import { IconButton, ArrowLeftIcon } from '@paypalcorp/pp-react'
import ButtonFullWidth from '../../components/ButtonFullWidth'
import serverData from '../../utils/server-data'
import { PrivacyContent } from './PrivacyContent'
import { Message } from '@paypalcorp/worldready-react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  selectIntroSheetState,
  selectSheetSource,
  setCreateGroupFlow,
  toggleIntro,
} from '../../store/slices/sheetsSlice'
import SheetWrapper from '../../components/SheetWrapper'
import { useMatch } from '@reach/router'
import {
  getContributeGuestURL,
  getContributionUserURL,
} from '../../utils/links/contributeLinks'
import { IntroContent } from './IntroContent'
import { useLocalizedContent } from '../../hooks/useLocalizedContent'
import { HOME_PATH, SHEET_SOURCE } from '../../constants'
import { getGroupDetailPageVariant, tracking } from '../../lib/analytics/fpti'
import Events from '../../lib/analytics/eventTags.js'
import IntroUserAgnosticContent from './IntroUserAgnosticContent'
import IntroUserSpecificEaseOfUser from './IntroUserSpecificEaseOfUser'
import IntroUserSpecificTransparency from './IntroUserSpecificTransparency'
import {
  isUserSpecificEaseOfUseIntroEnabled,
  isUserSpecificTransparencyEnabled,
  isUserTypeAgnosticIntroEnabled,
} from '../../utils/features'
import { TrafficSourceKey } from '../../../typings'

const Intro = () => {
  const { getContent } = useLocalizedContent()
  const isSheetOpened = useAppSelector(selectIntroSheetState)
  const dispatch = useAppDispatch()
  const [isPrivacyShown, setIsPrivacyShown] = useState(false)
  const { groupId } = useMatch(`${HOME_PATH}/:groupId`) || { groupId: '' }

  const [isLoading, setIsLoading] = useState(false)
  const isPayPalUser = !!serverData?.user
  const isAdmin = groupId.length === 0 && isPayPalUser // making the assumption that groupId='' is a Creator
  const pageVariant = getGroupDetailPageVariant(isAdmin, isPayPalUser)
  const sheetSource = useAppSelector(selectSheetSource)
  const source = serverData.trackingParameters
    .externalSource as TrafficSourceKey

  useEffect(() => {
    const impressionLink = serverData?.personalization?.firstTimeUser?.link
    if (impressionLink && isSheetOpened) {
      //fire impression event
      new Image().src = impressionLink
      // The intro screen should be displayed only once.
      // Making the link null prevents the sheet being displayed again after closing it.
      serverData.personalization.firstTimeUser.link = null
    }
    tracking(
      Events.p2p_pools_intro_screen_shown(serverData.trackingParameters.intro),
    )
  }, [isSheetOpened])

  const goToPrivacyScreen = () => {
    tracking(
      Events.p2p_pools_intro_screen_learn_more_pressed({
        ...serverData.trackingParameters.intro,
        page_variant: pageVariant,
      }),
    )
    setIsPrivacyShown(true)
  }

  const goToIntroScreen = () => {
    setIsPrivacyShown(false)
  }

  const closeSheet = () => {
    dispatch(toggleIntro())
  }
  const getButtonContentId = () => {
    if (isPrivacyShown) {
      return 'privacyStatement.confirmButton'
    } else if (sheetSource === 'createButton') {
      return 'create.intro.startPooling'
    }
    return 'create.intro.contribute'
  }
  const onClickConfirmButton = () => {
    if (!isPrivacyShown) {
      tracking(
        Events.p2p_pools_intro_screen_shown_start_pooling_pressed({
          ...serverData.trackingParameters.intro,
          page_variant: pageVariant,
          pool_id: groupId,
        }),
      )
    }
    const shouldNavigateToCreateFlow =
      !groupId || sheetSource === SHEET_SOURCE.CREATE_GROUP_BUTTON
    const isGuest = !serverData?.user
    setIsLoading(true)
    if (shouldNavigateToCreateFlow) {
      closeSheet()
      dispatch(setCreateGroupFlow({ toggleCreateGroupFlow: true }))
    } else if (isGuest) {
      window.location.assign(getContributeGuestURL(groupId, source))
    } else {
      window.location.assign(getContributionUserURL(groupId, source))
    }
  }
  const onClickBack = () => {
    if (isPrivacyShown) {
      goToIntroScreen()
    } else {
      tracking(
        Events.p2p_pools_intro_screen_back_pressed({
          ...serverData.trackingParameters.intro,
          page_variant: pageVariant,
          pool_id: groupId,
        }),
      )
      closeSheet()
    }
  }

  const renderFooterContents = (
    <ButtonFullWidth data-testid="confirmButton" onClick={onClickConfirmButton}>
      <Message id={getButtonContentId()} />
    </ButtonFullWidth>
  )
  const renderIntroContent = () => {
    if (isUserTypeAgnosticIntroEnabled()) {
      return (
        <IntroUserAgnosticContent
          goToPrivacyScreen={goToPrivacyScreen}
          onClickConfirmButton={onClickConfirmButton}
        />
      )
    }

    if (isUserSpecificEaseOfUseIntroEnabled()) {
      return (
        <IntroUserSpecificEaseOfUser
          goToPrivacyScreen={goToPrivacyScreen}
          onClickConfirmButton={onClickConfirmButton}
        />
      )
    }

    if (isUserSpecificTransparencyEnabled()) {
      return (
        <IntroUserSpecificTransparency
          goToPrivacyScreen={goToPrivacyScreen}
          onClickConfirmButton={onClickConfirmButton}
        />
      )
    }

    return (
      <IntroContent
        goToPrivacyScreen={goToPrivacyScreen}
        pageVariant={pageVariant}
      />
    )
  }
  const isNewIntroExperiment =
    isUserTypeAgnosticIntroEnabled() ||
    isUserSpecificEaseOfUseIntroEnabled() ||
    isUserSpecificTransparencyEnabled()

  const showBackButton = isPrivacyShown || !isNewIntroExperiment
  let action = null
  if (showBackButton) {
    action = (
      <IconButton
        data-testid="backButton"
        svgIconComponent={ArrowLeftIcon}
        buttonType="tertiary"
        onClick={onClickBack}
      />
    )
  }

  return (
    <SheetWrapper
      contentScrollTop={isPrivacyShown ? 2 : 1}
      center={false}
      isOpen={isSheetOpened}
      isLoading={isLoading}
      title={getContent('create.intro.sheetTitle')}
      noCloseButton={isPrivacyShown}
      hideTitle={true}
      onCloseComplete={closeSheet}
      footerContents={showBackButton && renderFooterContents}
      data-testid="intro-sheet"
      mobileFixedHeight="100"
      action={action}
    >
      {isPrivacyShown ? <PrivacyContent /> : renderIntroContent()}
    </SheetWrapper>
  )
}

export default Intro
